/**
 * Utility functions for role-based access control
 */

/**
 * Check if a user has admin access
 * Uses the new 'role' field if available, falls back to 'primaryRole' for backward compatibility
 */
export const hasAdminAccess = (user: any): boolean => {
  if (!user) return false;
  
  // Check new role field (preferred)
  if (user.role === 'admin') {
    return true;
  }
  
  // Fallback to primaryRole for backward compatibility
  if (user.primaryRole === 'admin' || user.primaryRole === 'app_admin') {
    return true;
  }
  
  // Check roles array (legacy)
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes('admin') || user.roles.includes('app_admin');
  }
  
  return false;
};

/**
 * Check if a user has a specific role (either through primaryRole or roles array)
 */
export const hasRole = (user: any, role: string): boolean => {
  if (!user) return false;
  
  // Check primary role
  if (user.primaryRole === role) {
    return true;
  }
  
  // Check roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(role);
  }
  
  return false;
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (user: any, roles: string[]): boolean => {
  if (!user) return false;
  
  // Check primary role
  if (roles.includes(user.primaryRole)) {
    return true;
  }
  
  // Check roles array
  if (user.roles && Array.isArray(user.roles)) {
    return roles.some(role => user.roles.includes(role));
  }
  
  return false;
};

// Property access by sale type: rental-only vs sales-only vs admin (both)
const RENTAL_ONLY_ROLES = ['property_manager', 'rental_agent', 'rental_assistant'];
const SALES_ONLY_ROLES = ['sales_manager', 'sales_agent', 'sales_assistant'];
const ADMIN_PROPERTY_ROLES = ['admin', 'app_admin'];

export type PropertyViewMode = 'rental' | 'sales' | 'both';

/**
 * Determines how the user can view properties: rental-only, sales-only, or both (admin).
 * Uses ALL roles (primaryRole + roles array) so types adjust automatically to the user's role.
 * Used for Properties list tabs and API saleType filter.
 */
export const getPropertyViewMode = (user: any): PropertyViewMode => {
  if (!user) return 'both';
  const primary = user.primaryRole as string | undefined;
  const roles = (user.roles || []) as string[];
  const allRoles = primary ? [primary, ...roles] : roles;
  const unique = [...new Set(allRoles.filter(Boolean))];
  if (unique.length === 0) return 'both';
  const isAdmin =
    user.role === 'admin' ||
    unique.some((r: string) => ADMIN_PROPERTY_ROLES.includes(r));
  if (isAdmin) return 'both';
  const hasRental = unique.some((r: string) => RENTAL_ONLY_ROLES.includes(r));
  const hasSales = unique.some((r: string) => SALES_ONLY_ROLES.includes(r));
  if (hasRental && hasSales) return 'both';
  if (hasRental) return 'rental';
  if (hasSales) return 'sales';
  return 'both'; // photographer, styler, etc. – no restriction
};

/** True if user can only manage rentals (property list and create restricted to rentals). */
export const canOnlyManageRentals = (user: any): boolean => getPropertyViewMode(user) === 'rental';

/** True if user can only manage sales (property list and create restricted to sales). */
export const canOnlyManageSales = (user: any): boolean => getPropertyViewMode(user) === 'sales';

/** True if user can see both sales and rentals (admin) – show Sales | Rentals tabs. */
export const canManageBothSalesAndRentals = (user: any): boolean => getPropertyViewMode(user) === 'both';
